/**
 * Float utility functions for precise floating-point operations
 *
 * This module provides functions for rounding and comparing floating-point numbers
 * and vectors with consistent precision, addressing common floating-point issues.
 */
/**
 * Rounds a number to a specified decimal precision
 *
 * @param n - The number to round
 * @param p - The number of decimal places to round to
 * @returns The rounded number
 *
 * @example
 * roundFloat(0.167, 2) // Returns 0.17
 * roundFloat(0.167, 1) // Returns 0.2
 */
export function roundFloat(n, p) {
    const precision = Math.pow(10, p);
    return Math.round(n * precision) / precision;
}
/**
 * Rounds a number to handle floating-point precision errors
 *
 * Given a number n, rounds it to the nearest number representable in the
 * floating-point precision bucket one size larger than the minimum, so
 * that operations with round decimals stay representable as round
 * decimals, while preserving all (or most) significant digits irrespective
 * of the magnitude of the number.
 *
 * @param n - The number to round
 * @returns The rounded number with floating-point errors corrected
 *
 * @example
 * roundFloatError(0.1 + 0.2) // Returns 0.3 instead of 0.30000000000000004
 * roundFloatError(100_000_000_000.1 + 100_000_000_000.2) // Returns 200_000_000_000.3
 *
 * @remarks
 * This function is very slow due to the `toPrecision` round trip which (among other
 * things) converts the number to a string and back. Also, the purpose of this function is often to
 * compare floats reliably across different magnitudes, and a more common (and probably better, more
 * well-documented) way to compare floats is with a relative tolerance. In many cases, you probably
 * want to use compareFloats() instead!
 */
export function roundFloatError(n) {
    return Number(n.toPrecision(15));
}
/**
 * Rounds both components of an IVector2 to a specified decimal precision
 *
 * @param vector - The IVector2 to round
 * @param p - The number of decimal places to round to
 * @returns A new IVector2 object with rounded components
 *
 * @example
 * roundVec2Float({x: 0.123, y: 0.167}, 2) // Returns {x: 0.12, y: 0.17}
 */
export function roundVec2Float(vector, p) {
    return { x: roundFloat(vector.x, p), y: roundFloat(vector.y, p) };
}
/**
 * Rounds both components of an IVector2 to handle floating-point precision errors
 *
 * @param vector - The IVector2 to round
 * @returns A new IVector2 object with components rounded to handle floating-point errors
 *
 * @example
 * roundVec2FloatError({x: 0.1 + 0.2, y: 0.3}) // Returns {x: 0.3, y: 0.3}
 *
 * @remarks
 * See remarks for roundFloatError() for performance considerations
 */
export function roundVec2FloatError(vector) {
    return { x: roundFloatError(vector.x), y: roundFloatError(vector.y) };
}
/**
 * Compares two floating-point numbers for approximate equality
 *
 * This function handles the common issues with floating-point comparisons by using
 * both relative and absolute tolerance values. It's particularly useful for comparing
 * numbers of different magnitudes.
 *
 * @param a - First number to compare
 * @param b - Second number to compare
 * @param opts - Comparison options (relTolerance and absTolerance)
 * @returns True if the numbers are considered equal within the specified tolerances
 *
 * @example
 * compareFloats(0.1 + 0.2, 0.3) // Returns true despite floating-point errors
 * compareFloats(200, 201, {relTolerance: 0.01}) // Returns true (within 1% relative tolerance)
 *
 * @remarks
 * - If you specify an absTolerance of exactly 0, then this function will never compare a
 *   number as "close" to zero, no matter how small, because the relTolerance is always smaller than
 *   the absolute difference.
 * - If you specify absTolerance > relTolerance, then relTolerance is effectively ignored.
 *
 * Inspired by Python's math.isclose():
 * https://peps.python.org/pep-0485/
 */
export function compareFloats(a, b, opts = {}) {
    const relTolerance = opts.relTolerance ?? 1e-9;
    const absTolerance = opts.absTolerance ?? 1e-12;
    const diff = Math.abs(a - b);
    // Always apply relative tolerance to the largest of a and b, so this function returns the same
    // give different results depending on eg codepath!
    return diff <= Math.max(Math.abs(a), Math.abs(b)) * relTolerance || diff <= absTolerance;
}
/**
 * Compares two IVector2 objects for approximate equality
 *
 * @param a - First vector to compare
 * @param b - Second vector to compare
 * @param opts - Comparison options (relTolerance and absTolerance)
 * @returns True if both x and y components are considered equal within the specified tolerances
 *
 * @example
 * compareVec2({x: 0.1 + 0.2, y: 0.3}, {x: 0.3, y: 0.3}) // Returns true despite floating-point errors
 */
export function compareVec2(a, b, opts = {}) {
    return compareFloats(a.x, b.x, opts) && compareFloats(a.y, b.y, opts);
}
//# sourceMappingURL=float.js.map